import { AnalysisScopeSwitch } from '@modules/analysis/components/AnalysisScopeSwitch';
import { useUpdateAnalysis } from '@modules/analysis/hooks/useUpdateAnalysis';
import { AnalysisStoreOptions } from '@modules/analysis/store/types';
import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { EscHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { Option } from '@modules/common/types';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  DialogContent,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PermissionOperation } from 'generated/graphql';
import { FormEvent, SyntheticEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scope } from 'v2/core/entities/scope';
import { SuperPermissionResource } from 'v2/core/entities/user';
import { mapToScope } from 'v2/core/repository/edge-server/analysis/toEdgeAnalysisMappers';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useListUsers } from 'v2/core/services/listUsers';
import { useGetUser } from 'v2/core/services/user';

interface ChangeAnalysisOwnerFormProps {
  analysis: AnalysisStoreOptions;
}

export const ChangeAnalysisOwnerForm = ({
  analysis,
}: ChangeAnalysisOwnerFormProps) => {
  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const navigate = useNavigate();
  const { closeModal } = useMuiModalContext();
  const { data, status, error } = useListUsers();

  const { id, title, editToken, createdBy, resetToEmptyAnalysis } = analysis;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedScope, setSelectedScope] = useState(Scope.PUBLIC);

  const canAccessDiscoverability = useIsAuthorized({
    resource: SuperPermissionResource.Discoverability,
    operation: PermissionOperation.Write,
  });

  const {
    updateAnalysis,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateAnalysis({
    name: title,
    scope: selectedScope,
    id: id,
    editToken: editToken,
    createdBy: createdBy,
  });

  const onTransferOwnership = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await updateAnalysis({
        variables: {
          title,
          scope: mapToScope(selectedScope),
          id,
          editToken: editToken ?? '',
          createdBy: selectedUser,
          tenantID,
        },
      });
      showSuccessNotification({
        contentComponent: `Quantify ownership transferred successfully!`,
        props: { isClosable: true },
      });
      navigate(`/analysis`);
      resetToEmptyAnalysis();
      closeModal();
    } catch {
      showErrorNotification({
        contentComponent: `Failed to transfer ownership of the Quantify.`,
        props: { isClosable: true },
      });
    }
  };
  const handleOnSelectUser = (_: SyntheticEvent, selected: Option | null) => {
    if (!selected) {
      return;
    }
    setSelectedUser(selected.value);
  };

  const handleScopeSwitchChange = (checked: boolean) => {
    setSelectedScope(checked ? Scope.PUBLIC : Scope.PRIVATE);
  };

  const isFetchingUsers = IsLoading(status);

  const selectUserOptions =
    data?.users?.map((user) => ({
      label: `${user.firstName} ${user.lastName} - (${user.email})`,
      value: user.id,
    })) ?? [];
  const selectedUserValue = selectUserOptions.find(
    (option) => option.value === selectedUser,
  );

  if (error || updateError) {
    return (
      <Typography color="error">
        Failed to initialize Quantify actions
      </Typography>
    );
  }

  return (
    <>
      <ModalHeader
        title={`Transfer Quantify Ownership`}
        showCloseButton={false}
      />
      <Box component="form" onSubmit={onTransferOwnership}>
        <DialogContent sx={{ p: 0 }}>
          <Stack pb={4} pt={2} spacing={3}>
            <Box px={3}>
              <Autocomplete
                fullWidth
                options={selectUserOptions}
                getOptionLabel={(option) => option.label}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select User" />
                )}
                value={selectedUserValue}
                onChange={handleOnSelectUser}
                loading={isFetchingUsers}
              />
            </Box>
            {canAccessDiscoverability && (
              <Box px={3}>
                <AnalysisScopeSwitch
                  scope={selectedScope}
                  onChange={handleScopeSwitchChange}
                  type="analysis"
                  isLoading={isUpdating}
                />
              </Box>
            )}
            <Divider />
            <Stack
              spacing={1}
              px={3}
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button
                variant="outlined"
                onClick={() => closeModal()}
                disabled={isLoading}
                endIcon={<EscHotKeyIcon isDisabled={isLoading} />}
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                disabled={!selectedUser || isUpdating || isLoading}
                loading={isLoading}
              >
                Transfer
              </LoadingButton>
            </Stack>
          </Stack>
        </DialogContent>
      </Box>
    </>
  );
};
